/**
 * The lists-and-items model: the shape both ends speak, and the one rule about
 * the order they are shown in.
 *
 * It used to be the web client's data path as well — a function per REST route.
 * Step 7 took that away rather than wrapping it: screens read from the local
 * mirror in `src/data/` and write to a queue that drains through `/api/sync`,
 * and a second way in would be a second chance for the two to disagree. The
 * REST routes are untouched and are the agent surface, which is why these types
 * are still described in their terms.
 */

import type { Recurrence, Repeat } from "./recurrence.js";
import type { ListOrder } from "../../api/src/shared/list-order.js";
import { compareOrder } from "../../api/src/shared/ordering.js";
export type { ListOrder } from "../../api/src/shared/list-order.js";
export type ListKind = "todo" | "shopping" | "other";

export const LIST_KINDS: ListKind[] = ["todo", "shopping", "other"];

/**
 * A calendar fact, not an instant. `time` and `tz` are set together or not at
 * all — the server refuses one without the other, and so should any caller.
 */
export interface Due {
  date: string;
  time: string | null;
  tz: string | null;
}

export interface List {
  id: string;
  kind: ListKind;
  name: string;
  /**
   * Who made it (g68): an opaque identity id from the server, whose name is in
   * the mirror's creators. Absent on a row made here and not yet echoed back,
   * and null only on a row the server has never credited.
   */
  creatorId?: string | null;
  orderKey: string;
  /**
   * How the list is shown (g81): "category" while Order by category is on.
   * Optional: a mirror row from before g81 has none, which reads as off.
   * Changed only through the store's `setListOrder`, never by a patch.
   */
  orderBy?: ListOrder | null;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
  /**
   * Whether its view or edit link works (g71), from the owner's own sync. Two
   * booleans and never a link. Absent on a row made here and not yet echoed,
   * and always on a shared list as a visitor holds it.
   */
  sharing?: ListSharing;
}

export interface ListSharing {
  view: boolean;
  edit: boolean;
}

export interface Item extends Recurrence {
  id: string;
  listId: string;
  /** As `List.creatorId`. */
  creatorId?: string | null;
  text: string;
  done: boolean;
  quantity: string | null;
  /** Free text such as an aisle (g79). Optional: a mirror row from before g79 has none. */
  category?: string | null;
  note: string | null;
  due: Due | null;
  /**
   * When it was last completed, by whose clock, and the server's record of it
   * (g105). The server's alone: a device never sets them, and a row ticked
   * here keeps what it had until its echo arrives. Null is not known. On a
   * repeating item it is the last occurrence completed. Optional: a mirror
   * row from before g105 has none. Nothing shows them yet (g110).
   */
  completedAt?: number | null;
  completedAtSource?: "device" | "server" | null;
  completionEventId?: string | null;
  orderKey: string;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
}

/**
 * Who made something (g68), as a sync delta names them: an opaque id and the
 * name they chose, or none. A deleted account keeps its id and loses its name.
 * Never an email: a client with no name to show shows none.
 */
export interface Creator {
  id: string;
  name: string | null;
  deleted?: true;
}

export interface ListPatch {
  name?: string;
  kind?: ListKind;
}

export interface ItemInput {
  text: string;
  repeat?: Repeat | null;
  quantity?: string | null;
  category?: string | null;
  note?: string | null;
  due?: Due | null;
}

/**
 * Where a row goes, named as neighbours rather than as a key.
 *
 * The server generates every order key and accepts none, so this is the whole
 * vocabulary a client has for position. Both neighbours are sent: either can be
 * deleted on another device while the mutation sits in the queue, and the
 * survivor is what keeps the change meaningful instead of rejected.
 */
export interface Anchors {
  afterId?: string | null;
  beforeId?: string | null;
}

/**
 * A patch omits what it does not touch and sends `null` to clear — the server
 * draws that line too, and `undefined` disappears in `JSON.stringify`, which is
 * exactly the behaviour wanted.
 */
export interface ItemPatch extends Partial<ItemInput> {
  done?: boolean;
}

/**
 * The server's order, reproduced.
 *
 * Duplicating a server rule on the client is usually how the two drift apart,
 * but the client is the only one that can apply it at the right moment: the
 * mirror is written long before the server is asked, and an order that only
 * arrived with the next delta would leave a new row sitting at the bottom until
 * the network came back.
 *
 * The rule is now one comparison, and the sink that used to precede it is gone:
 * ticking an item leaves it exactly where it is. Sorting done items to the
 * bottom was itself a re-rank nobody asked for, and it made "insert after this
 * one" meaningless for any anchor that moved the moment it was ticked.
 *
 * Keys are compared as strings. They are base-62 and lexicographic by
 * construction — comparing them numerically is the mistake that would sort
 * 'b00' above 'az'.
 */
export function inDisplayOrder<T extends { id: string; orderKey: string }>(items: T[]): T[] {
  // The id breaks a tied key, so every device reads the same order.
  return [...items].sort(compareOrder);
}
